import { useEffect, useRef } from 'react'
import { Map } from 'ol'
import { toLonLat } from 'ol/proj'
import { findLayerByName } from '../../utils/findLayerByName'
// import { addGeolocationLayer } from '../../utils/geolocationLayer'
// import { debugMapLayers } from '../../debugFunctions/debugMapLayers'
import encodeAfacodes from 'CitizenPortal/services/calls/geo/encodeAfacodes'
import { OpenLayersBaseMap } from '../../../../types'
import { createPinsLayer } from '../../utils/createPinsLayer'
import { createAFACodePinLayer } from '../../utils/createAFACodePinLayer'
import { getPolygonCenter } from '../../utils/getPolygonCenter'
import { moveMapView } from '../../utils/moveMapView'
import { INSTITUTION_SLUG } from 'constants/system'
import { applyFeatureStyle } from '../../utils/applyFeatureStyle'

export function useInitializeMap({
  mapRef,
  mapInstance,
  entityLayer,
  view,
  baseLayerSourceName,
  createBaseLayer,
  // geolocation,
  selectedFeatureRef,
  hoveredFeatureRef,
  _getMarkingData,
  AFACodeHandleClick,
  pins,
  openDrawer,
}: {
  mapRef: React.RefObject<HTMLDivElement>
  mapInstance: React.MutableRefObject<Map | null>
  entityLayer: any
  view: any
  baseLayerSourceName: OpenLayersBaseMap
  createBaseLayer: () => any
  // geolocation: { active: boolean }
  selectedFeatureRef: any
  hoveredFeatureRef: any
  _getMarkingData: any
  AFACodeHandleClick?: boolean
  pins?: {
    id: string
    lat: number
    lon: number
    type: string
    data: any
  }[]
  openDrawer: any
}) {
  const clickHandlerRef = useRef<any>(null)

  // Carrega a API do Google Maps dinamicamente
  const loadGoogleMapsAPI = () => {
    const script = document.createElement('script')
    script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyAKbWMjgLtI1WzPiO8psJ4SDX3SXhLBMfw&callback=initMap'
    document.head.appendChild(script)
  }

  loadGoogleMapsAPI()

  // Criação única do mapa
  useEffect(() => {
    if (!mapRef.current) return

    const map = new Map({ target: mapRef.current, view })
    mapInstance.current = map

    // Camada base
    const baseLayer = createBaseLayer()
    baseLayer.set('name', 'BASE_MAP')
    baseLayer.set('sourceName', baseLayerSourceName)
    baseLayer.setZIndex(0)
    map.addLayer(baseLayer)
    // Camada das entidades
    map.addLayer(entityLayer)
    // Geolocalização
    // if (geolocation?.active) {
    //   addGeolocationLayer(map)
    // }

    return () => map.setTarget(undefined)
  }, []) // só executa uma vez

  useEffect(() => {
    const map = mapInstance.current
    if (!map) return

    // Remove handler antigo se existir
    if (clickHandlerRef.current) {
      map.un('click', clickHandlerRef.current)
      clickHandlerRef.current = null
    }

    function removeAllAFACodeLayers(map: any) {
      const allLayers = map.getLayers().getArray()

      // Criar array de layers a remover
      const layersToRemove: any[] = []

      allLayers.forEach((layer: any) => {
        const layerName = layer.get('name')

        // Remover qualquer layer que contenha "AFACODE" no nome
        if (layerName && layerName.includes('AFACODE')) {
          layersToRemove.push(layer)
        }
      })

      // Remover todos os layers AFACODE
      layersToRemove.forEach((layer) => {
        map.removeLayer(layer)
      })
    }

    const afaCodeHandleClick = async (event: any) => {
      const feature: any = map.forEachFeatureAtPixel(event.pixel, (f, layer) => {
        const layerName = layer?.get('name')

        // ✅ Detectar PIN OU SHAPE (qualquer coisa do AFACODE)
        if (layerName === 'ENTITY_PINS' || layerName?.includes('AFACODE')) {
          return f
        }
      })

      // Se clicou em PIN ou SHAPE, sai da função
      if (feature) {
        return
      }

      const [lon, lat] = toLonLat(event.coordinate)

      try {
        removeAllAFACodeLayers(map)

        const res = await encodeAfacodes({
          data: { lat, lng: lon, u: INSTITUTION_SLUG == 'mocambique' ? '2' : '4' },
        })

        const { area, side } = res.body.afacode.properties
        const logistic_id = res.body.afacode.id
        const geometry = res.body.afacode.geometry

        const { centerLat, centerLon } = getPolygonCenter(geometry)

        // Cria nova layer com os pins
        const newPin = {
          id: logistic_id,
          lat: centerLat,
          lon: centerLon,
          type: 'afacode',
          data: {
            area: area,
            side: side,
            logistic_id: logistic_id,
            geometry: geometry,
            lat: centerLat,
            lon: centerLon,
          },
        }
        const afaLayer = createAFACodePinLayer({ pin: newPin })
        if (afaLayer) {
          afaLayer.setZIndex(999)
          map.addLayer(afaLayer)
        }
        //MOVIMENTANDO O MAPA PARA O PONTO CLICADO
        moveMapView({ map, view, lat, lon })

        //ABRINDO ENTIDADE AFACODE NO DRAWER
        // openDrawer({ type: 'afacode', data: { area: area, side: side, logistic_id: logistic_id, geometry: geometry } })

        //CHAMANDO FUNÇÃO DE PREENCHER ENDEREÇO
        _getMarkingData({ lat, lon })
      } catch (err) {
        console.error('Erro ao buscar polígono:', err)
      }
    }

    // Quando o usuário parar de mover o mapa
    map.on('moveend', () => {
      const view = map.getView()
      const center = view.getCenter()
      if (!center) return
      const [lon, lat] = toLonLat(center)
      _getMarkingData({ lat, lon })
    })

    map.on('pointermove', (event) => {
      if (event.dragging) return

      const feature: any = map.forEachFeatureAtPixel(event.pixel, (f, layer) => {
        if (layer?.get('name') === 'ENTITY_PINS' || layer?.get('name').includes('AFACODE_PIN')) return f
      })

      const target = map.getTargetElement()

      if (feature) {
        target.style.cursor = 'pointer'

        // Se já está selecionado, não aplicar hover
        if (feature === selectedFeatureRef.current) return

        // Resetar hover anterior
        if (hoveredFeatureRef.current) {
          applyFeatureStyle(hoveredFeatureRef.current)
        }

        // Aplicar hover
        applyFeatureStyle(feature, { hover: true })

        hoveredFeatureRef.current = feature
      } else {
        target.style.cursor = ''

        // Resetar hover ao sair
        if (hoveredFeatureRef.current) {
          applyFeatureStyle(hoveredFeatureRef.current)
          hoveredFeatureRef.current = null
        }
      }
    })

    //Controle de clique
    map.on('click', (event) => {
      let featureFound = false
      let clickedFeature: any = null
      let clickedLayer: any = null

      map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
        if (layer?.get('name') === 'ENTITY_PINS' || layer?.get('name').includes('AFACODE')) {
          clickedFeature = feature
          clickedLayer = layer
          return true
        }
      })

      if (clickedFeature) {
        featureFound = true
        const layerName = clickedLayer?.get('name')

        //VERIFICA SE CLICOU NO PIN
        if (layerName?.includes('PIN')) {
          const type = clickedFeature.get('type')
          const data = clickedFeature.get('data')

          // 🔁 Resetar anterior se existir
          if (selectedFeatureRef.current) {
            applyFeatureStyle(selectedFeatureRef.current)
          }

          // ⭐ Aplicar estilo selecionado no atual
          applyFeatureStyle(clickedFeature, { selected: true })

          // 💾 Salvar referência
          selectedFeatureRef.current = clickedFeature

          // 🔥 Abrir drawer
          openDrawer({ type, data })

          //MOVIMENTANDO O MAPA PARA O PONTO CLICADO
          moveMapView({ map, view, lat: data?.lat, lon: data?.lon })
        }
      }

      // 👉 Clique fora dos pins
      if (!featureFound) {
        // Resetar seleção visual
        if (selectedFeatureRef.current) {
          applyFeatureStyle(selectedFeatureRef.current)
          selectedFeatureRef.current = null
        }

        if (AFACodeHandleClick) {
          afaCodeHandleClick(event)
        }
      }
    })
  }, []) // apenas atualiza lógica de clique

  useEffect(() => {
    const map = mapInstance.current
    if (!map || !mapRef.current) return

    const observer = new ResizeObserver(() => {
      map.updateSize()
    })

    observer.observe(mapRef.current)

    return () => observer.disconnect()
  }, [mapInstance])

  useEffect(() => {
    const map = mapInstance.current
    if (!map || !pins) return

    // Remove layer antiga se existir
    const oldLayer = findLayerByName(mapInstance, 'ENTITY_PINS')
    if (oldLayer) {
      map.removeLayer(oldLayer)
    }

    // Cria nova layer com os pins
    const pinsLayer = createPinsLayer(pins)
    pinsLayer.setZIndex(999)

    map.addLayer(pinsLayer)
  }, [pins])
}
